// Placar (DESIGN §5.15): o único bloco ousado da tela — placa de tinta com corte-hero. UM por tela.
// Variantes pelo conteúdo: pontos (Início), série (Execução), posição (Ranking), unidade (Visão geral), saldo (Extrato).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { formatarNumero } from "../lib/numeros";
import { Icones } from "./icones";

export interface PropsPlacar {
  /** Rótulo do topo ("Seu placar", "Hoje na unidade"). Vira aria-label da seção. */
  rotulo: string;
  /** Número protagonista (text-display placar) ou nó pronto. */
  valor?: ReactNode;
  /** Unidade ao lado do número ("pts", "de 340"). */
  unidade?: ReactNode;
  /** Linha de apoio abaixo ("Nível Forte · 320 pts para Elite"). */
  apoio?: ReactNode;
  /** Barra, segmentos etc. */
  children?: ReactNode;
  /** Faixa inferior com 2 (aluno) ou 4 (painel) colunas. */
  faixa?: { valor: ReactNode; rotulo: ReactNode }[];
  className?: string;
}

export function Placar({ rotulo, valor, unidade, apoio, children, faixa, className }: PropsPlacar) {
  return (
    <section
      aria-label={rotulo}
      className={cn("corte-hero rounded-placa bg-tinta p-5 pb-4 text-sobre-tinta", className)}
    >
      <p className="text-rotulo text-sobre-tinta-2">{rotulo}</p>
      {valor !== undefined && (
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <span className="text-display placar">{valor}</span>
          {unidade && (
            <span className="text-h2 font-semibold text-sobre-tinta-2 [font-stretch:100%]">{unidade}</span>
          )}
        </p>
      )}
      {apoio && <p className="mt-1 text-apoio text-sobre-tinta-2">{apoio}</p>}
      {children && <div className="mt-3">{children}</div>}
      {faixa && faixa.length > 0 && (
        <dl
          className={cn(
            "mt-4 grid border-t border-tinta-2 pt-3",
            faixa.length > 2 ? "grid-cols-2 gap-y-3 lg:grid-cols-4" : "grid-cols-2",
          )}
        >
          {faixa.map((f, i) => (
            <div
              key={i}
              className={cn(
                "flex flex-col-reverse",
                i % 2 === 1 && "border-l border-tinta-2 pl-4",
                faixa.length > 2 && i === 2 && "lg:border-l lg:pl-4",
              )}
            >
              <dt className="text-rotulo text-sobre-tinta-2">{f.rotulo}</dt>
              <dd className="text-numero placar">{f.valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Número que conta até o valor em 600 ms (movimento reduzido: vai direto). */
export function NumeroContando({
  valor,
  formatar = formatarNumero,
}: {
  valor: number;
  formatar?: (n: number) => string;
}) {
  const [exibido, setExibido] = useState(valor);
  const anterior = useRef(valor);
  useEffect(() => {
    const de = anterior.current;
    anterior.current = valor;
    const reduzido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduzido || de === valor) {
      setExibido(valor);
      return;
    }
    const inicio = performance.now();
    let quadro = 0;
    const passo = (t: number) => {
      const p = Math.min(1, (t - inicio) / 600);
      const suave = 1 - (1 - p) ** 3;
      setExibido(Math.round(de + (valor - de) * suave));
      if (p < 1) quadro = requestAnimationFrame(passo);
    };
    quadro = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(quadro);
  }, [valor]);
  return <>{formatar(exibido)}</>;
}

export interface PropsStatNumber {
  valor: ReactNode;
  rotulo: ReactNode;
  /** Variação opcional ("+12 vs. semana passada"). */
  variacao?: { texto: string; direcao: "subiu" | "desceu" | "neutra"; boa?: boolean };
  className?: string;
}

/** Número fora do placar: text-numero placar + rótulo pequeno embaixo. */
export function StatNumber({ valor, rotulo, variacao, className }: PropsStatNumber) {
  const Seta =
    variacao?.direcao === "subiu" ? Icones.subir : variacao?.direcao === "desceu" ? Icones.descer : null;
  return (
    <div className={cn("flex flex-col", className)}>
      <span className="text-numero placar text-tinta">{valor}</span>
      <span className="text-rotulo text-texto-3">{rotulo}</span>
      {variacao && (
        <span
          className={cn(
            "mt-1 inline-flex items-center gap-1 text-rotulo",
            variacao.boa === true ? "text-ok" : variacao.boa === false ? "text-erro" : "text-texto-2",
          )}
        >
          {Seta && <Seta size={14} aria-hidden />}
          {variacao.texto}
        </span>
      )}
    </div>
  );
}
